import Provider, { useProvider } from '@/common/Provider';
import React, { Dispatch, SetStateAction, useMemo, useRef } from 'react';

import { Dayts } from '@/utils';
import { InvoiceFormatEnum } from '@/models/gen/graphql';
import InvoicePreviewRow from '@/pages/InvoicePreview/components/InvoicePreviewRow';
import { InvoiceTripTypeEnum } from '@/models/gen/graphql';
import { PreviewTrip } from '@/pages/InvoicePreview';
import RateCell from '@/common/RateCell';
import SimpleTable from '@/components/SimpleTable';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import Table from '@/common/Table';
import { useClientIdsThatRequireCrew } from '@/api/services/providers/searchProviders';

type InvoicePreviewTableProps = {
  data?: PreviewTrip[];
  format?: InvoiceFormatEnum;
  payerProviderId?: string;
  onRefetch?: () => Promise<void>;
  combineIds?: string[];
  filteredTypes?: InvoiceTripTypeEnum[];
  hasDiscounts?: number;
};
export type InvoicePreviewTableProvider = {
  trips: Map<string, PreviewTrip>;
  format?: InvoiceFormatEnum;
  payerProviderId?: string;
  clientIdsThatRequireCrew?: string[];
  onRefetch?: () => Promise<void>;
  combineIds?: string[];
  filteredTypes?: InvoiceTripTypeEnum[];
  hasDiscounts?: number;
};
export type InvoicePreviewTableMethods = { onRefetch: () => Promise<void> };
export const useInvoicePreviewTable = <S,>(
  selector
): [S, Dispatch<SetStateAction<InvoicePreviewTableProvider>>, InvoicePreviewTableMethods] =>
  useProvider<InvoicePreviewTableProvider, S, InvoicePreviewTableMethods>(selector);

const InvoicePreviewTable = ({
  data,
  format,
  payerProviderId,
  onRefetch,
  filteredTypes = [],
  hasDiscounts,
}: InvoicePreviewTableProps): JSX.Element => {
  const tableRef = useRef();
  const [{ data: clientIdsThatRequireCrew }] = useClientIdsThatRequireCrew();

  const [tripIds, contextValue] = useMemo((): [string[], InvoicePreviewTableProvider] => {
    const trips = new Map();
    const combineIds = [];
    const tripIds = data
      .map((row: PreviewTrip): string => {
        if (row?.id) trips.set(row?.id, row);
        if (row?.id && row?.trip?.combineId && !combineIds.includes(row.trip.combineId)) combineIds.push(row.trip.combineId);
        return row?.id;
      })
      .filter((id: string): boolean => !!id);
    return [tripIds, { trips, format, payerProviderId, clientIdsThatRequireCrew, combineIds, filteredTypes, hasDiscounts }];
  }, [data, format, payerProviderId, clientIdsThatRequireCrew]);

  return (
    <Provider value={contextValue} methods={() => ({ onRefetch })}>
      <SimpleTable rows={tripIds} columns={InvoicePreviewTableHeader} row={InvoicePreviewRow} ref={tableRef} />
    </Provider>
  );
};

const InvoicePreviewTableHeader = (): JSX.Element => {
  const [[format, hasDiscounts]] = useProvider((state: InvoicePreviewTableProvider): [InvoiceFormatEnum, number] => [
    state.format,
    state.hasDiscounts,
  ]);
  return (
    <SimpleTableRow header>
      <SimpleTableCell name="INDEX">No.</SimpleTableCell>
      <SimpleTableCell name="TYPE">Type</SimpleTableCell>
      <SimpleTableCell name="SCHEDULED_DATE">Date</SimpleTableCell>
      <SimpleTableCell name="SCHEDULED_TIME">Time</SimpleTableCell>
      <SimpleTableCell name="IMPORT_TIME">Curb</SimpleTableCell>
      <SimpleTableCell name="AIRPORT_CODE">City</SimpleTableCell>
      <SimpleTableCell name="SERVICER_IATA_AIRLINE_CODE">Airline</SimpleTableCell>
      <SimpleTableCell name="FLIGHT_NUMBER">FLT</SimpleTableCell>
      {format === InvoiceFormatEnum.Crew && <SimpleTableCell name="CREW_ID">Crew ID</SimpleTableCell>}
      <SimpleTableCell name="PILOTS">PLT</SimpleTableCell>
      <SimpleTableCell name="ATTENDANTS">FA</SimpleTableCell>
      <SimpleTableCell name="PU_LOCATION">P/U</SimpleTableCell>
      <SimpleTableCell name="DO_LOCATION">D/O</SimpleTableCell>
      {hasDiscounts === 1 && <SimpleTableCell name="SAVINGS">Savings</SimpleTableCell>}
      <SimpleTableCell name="RATE">Rate</SimpleTableCell>
    </SimpleTableRow>
  );
};

// TODO: Use this instead of the existing InvoicePreviewTable once RateCell is decoupled from old table
const UpdatedInvoicePreviewTable = ({ data, format, payerProviderId, onRefetch, combineIds }: InvoicePreviewTableProps): JSX.Element => {
  return (
    <Table className="InvoicePreviewTable" header={<UpdatedInvoicePreviewTableHeader format={format} />} showControls>
      {data.map(
        (row: PreviewTrip, index: number): JSX.Element =>
          row?.trip ? (
            <Table.Row id={row.id} key={row.id}>
              <Table.Cell className="INDEX">{index + 1}</Table.Cell>
              <Table.Cell className="TYPE">{row?.trip?.type}</Table.Cell>
              <Table.Cell className="SCHEDULED_DATE" data-placeholder="--">
                {row?.trip?.scheduled ? new Dayts(row?.trip?.scheduled).format('MM/DD/YYYY') : ''}
              </Table.Cell>
              <Table.Cell className="SCHEDULED_TIME" data-placeholder="--">
                {row?.trip?.scheduled ? new Dayts(row?.trip?.scheduled).format('HH:mm') : ''}
              </Table.Cell>
              <Table.Cell className="IMPORT_TIME" data-placeholder="--">
                {row?.trip?.importTime ? new Dayts(row?.trip?.importTime).format('HH:mm') : ''}
              </Table.Cell>
              <Table.Cell className="AIRPORT_CODE">{row?.trip?.airportCode}</Table.Cell>
              <Table.Cell className="SERVICER_IATA_AIRLINE_CODE">{row?.trip?.servicerIataAirlineCode}</Table.Cell>
              <Table.Cell className="FLIGHT_NUMBER">{row?.trip?.flightNumber?.toString?.()?.padStart?.(4, '0')}</Table.Cell>
              {format === InvoiceFormatEnum.Crew && <Table.Cell className="CREW_ID">{row?.trip?.crewId}</Table.Cell>}
              <Table.Cell className="PILOTS">{row?.trip?.pilots}</Table.Cell>
              <Table.Cell className="ATTENDANTS">{row?.trip?.attendants}</Table.Cell>
              <Table.Cell className="PU_LOCATION">{row?.trip?.puLocation}</Table.Cell>
              <Table.Cell className="DO_LOCATION">{row?.trip?.doLocation}</Table.Cell>
              <Table.Cell className="SAVINGS">{row?.trip?.savings}</Table.Cell>
              <Table.Cell className="RATE">
                // TODO: Decouple component from old table so that it's usable anywhere, rewire to fit the new table
                <RateCell id={row?.trip?.tripId} rate={row?.trip?.rate} selected={[]} onChange={console.log} />
              </Table.Cell>
            </Table.Row>
          ) : (
            <Table.Row key={row.id}>
              <Table.Cell className="SUBTOTAL" colSpan={13}>
                SUBTOTAL:
              </Table.Cell>
              <Table.Cell>{row?.subtotal}</Table.Cell>
            </Table.Row>
          )
      )}
    </Table>
  );
};

const UpdatedInvoicePreviewTableHeader = ({ format }): JSX.Element => {
  return (
    <Table.Row header>
      <Table.Cell className="INDEX">No.</Table.Cell>
      <Table.Cell className="TYPE">Type</Table.Cell>
      <Table.Cell className="SCHEDULED_DATE">Date</Table.Cell>
      <Table.Cell className="SCHEDULED_TIME">Time</Table.Cell>
      <Table.Cell className="IMPORT_TIME">Curb</Table.Cell>
      <Table.Cell className="AIRPORT_CODE">City</Table.Cell>
      <Table.Cell className="SERVICER_IATA_AIRLINE_CODE">Airline</Table.Cell>
      <Table.Cell className="FLIGHT_NUMBER">FLT</Table.Cell>
      {format === InvoiceFormatEnum.Crew && <Table.Cell className="CREW_ID">Crew ID</Table.Cell>}
      <Table.Cell className="PILOTS">PLT</Table.Cell>
      <Table.Cell className="ATTENDANTS">FA</Table.Cell>
      <Table.Cell className="PU_LOCATION">P/U</Table.Cell>
      <Table.Cell className="DO_LOCATION">D/O</Table.Cell>
      <Table.Cell className="SAVINGS">Savings</Table.Cell>
      <Table.Cell className="RATE">Rate</Table.Cell>
    </Table.Row>
  );
};

export default React.memo(InvoicePreviewTable);
